/*
 * StreakBadge.jsx
 * ---------------
 * Displays the current streak count for a checklist.
 * Shows a flame icon that pulses when the streak is active.
 * Upgrades to milestone icons at 7, 30, and 100 days.
 * Renders nothing if streak is 0.
 * Used on ChecklistCard and ChecklistDetail.
 */

import React, { useEffect, useState } from "react";

// ============================================
// MILESTONE CONFIG
// ============================================
const MILESTONES = [
  { threshold: 100, icon: "🏆", label: "Legendary", color: "var(--accent-gold)" },
  { threshold: 30,  icon: "💎", label: "Diamond",   color: "#60A5FA" },
  { threshold: 7,   icon: "⭐", label: "Weekly",    color: "var(--accent-gold)" },
  { threshold: 3,   icon: "🔥", label: "Streak",    color: "var(--accent-gold)" },
];

function getMilestone(streak) {
  return MILESTONES.find(m => streak >= m.threshold) || null;
}

// ============================================
// PULSE ANIMATION STYLE
// Injected once into the document head
// ============================================
const PULSE_STYLE = `
  @keyframes flamePulse {
    0%, 100% { transform: scale(1) rotate(-5deg); }
    50%       { transform: scale(1.2) rotate(5deg); }
  }
  @keyframes badgePop {
    0%   { transform: scale(0.5); opacity: 0; }
    70%  { transform: scale(1.1); opacity: 1; }
    100% { transform: scale(1);   opacity: 1; }
  }
`;

let styleInjected = false;
function injectStyle() {
  if (styleInjected) return;
  const style = document.createElement("style");
  style.textContent = PULSE_STYLE;
  document.head.appendChild(style);
  styleInjected = true;
}

// ============================================
// MAIN EXPORT
// ============================================
export default function StreakBadge({
  streak,
  size = "md",
  showLabel = false,
  animate = true,
  justReached = false
}) {
  const [popped, setPopped] = useState(false);

  useEffect(() => { injectStyle(); }, []);

  // Trigger pop animation when a milestone is just reached
  useEffect(() => {
    if (justReached) {
      setPopped(true);
      const timer = setTimeout(() => setPopped(false), 600);
      return () => clearTimeout(timer);
    }
  }, [justReached]);

  // Render nothing for zero streaks
  if (!streak || streak === 0) return null;

  const milestone = getMilestone(streak);
  const icon = milestone?.icon || "🔥";
  const color = milestone?.color || "var(--accent-gold)";

  // ============================================
  // SIZE VARIANTS
  // ============================================
  const sizes = {
    sm: { fontSize: "12px", iconSize: "14px", padding: "2px 6px", gap: "3px" },
    md: { fontSize: "13px", iconSize: "16px", padding: "3px 8px", gap: "4px" },
    lg: { fontSize: "15px", iconSize: "20px", padding: "4px 12px", gap: "6px" },
  };

  const sizeStyle = sizes[size] || sizes.md;

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: sizeStyle.gap,
        background: "var(--accent-gold-muted)",
        border: `1px solid ${color}`,
        borderRadius: "100px",
        padding: sizeStyle.padding,
        animation: popped ? "badgePop 600ms ease" : "none"
      }}
    >
      {/* Flame / milestone icon */}
      <span
        style={{
          fontSize: sizeStyle.iconSize,
          display: "inline-block",
          animation: animate && streak > 0
            ? "flamePulse 2s ease-in-out infinite"
            : "none",
          transformOrigin: "bottom center"
        }}
      >
        {icon}
      </span>

      {/* Streak number */}
      <span style={{
        fontFamily: "var(--font-body)",
        fontWeight: 700,
        fontSize: sizeStyle.fontSize,
        color,
        lineHeight: 1
      }}>
        {streak}
      </span>

      {/* Optional label */}
      {showLabel && milestone && (
        <span style={{
          fontFamily: "var(--font-body)",
          fontWeight: 500,
          fontSize: sizeStyle.fontSize,
          color: "var(--text-muted)",
          lineHeight: 1
        }}>
          {milestone.label}
        </span>
      )}

      {/* Day label */}
      {showLabel && (
        <span style={{
          fontFamily: "var(--font-body)",
          fontWeight: 400,
          fontSize: sizeStyle.fontSize,
          color: "var(--text-muted)",
          lineHeight: 1
        }}>
          {streak === 1 ? "day" : "days"}
        </span>
      )}
    </div>
  );
}